import { Alert, Result, Skeleton, Space, Typography } from 'antd';
import { useTranslation } from 'react-i18next';
import { BarahPayApiError, translateApiError } from '@barahpay/shared';
import type { ReactNode } from 'react';

/**
 * Renders an API failure with its Barah Pay error code and correlation id, so an operator can quote
 * one identifier to support and have the request found in the logs (spec sections 38 and 52).
 */
export function ApiErrorAlert({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  const { t } = useTranslation();

  if (!error) {
    return null;
  }

  const apiError = error instanceof BarahPayApiError ? error : null;

  // Translated from the error code; the server's own English sentence is kept below as a diagnostic,
  // next to the code and the correlation id an operator would quote to support.
  const message = translateApiError(error, t);
  const serverDetail = apiError?.message && apiError.message !== message ? apiError.message : null;

  return (
    <Alert
      type="error"
      showIcon
      message={t('common.error')}
      description={
        <Space direction="vertical" size={2} style={{ width: '100%' }}>
          <Typography.Text>{message}</Typography.Text>
          {serverDetail ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {serverDetail}
            </Typography.Text>
          ) : null}
          {apiError?.errorCode ? (
            <Typography.Text type="secondary" code>
              {apiError.errorCode}
            </Typography.Text>
          ) : null}
          {apiError?.correlationId ? (
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {t('common.correlationId')}: <Typography.Text copyable code>{apiError.correlationId}</Typography.Text>
            </Typography.Text>
          ) : null}
          {apiError?.validationErrors
            ? Object.entries(apiError.validationErrors).map(([field, messages]) => (
                <Typography.Text key={field} type="secondary" style={{ fontSize: 12 }}>
                  {field}: {messages.join(', ')}
                </Typography.Text>
              ))
            : null}
        </Space>
      }
      action={
        onRetry ? (
          <Typography.Link onClick={onRetry}>{t('common.retry')}</Typography.Link>
        ) : undefined
      }
      style={{ marginBottom: 16 }}
    />
  );
}

/** Wraps a query result: skeleton while loading, error alert on failure, children otherwise. */
export function QueryBoundary({
  isLoading,
  error,
  onRetry,
  children,
  rows = 6,
}: {
  isLoading: boolean;
  error: unknown;
  onRetry?: () => void;
  children: ReactNode;
  rows?: number;
}) {
  if (error) {
    return <ApiErrorAlert error={error} onRetry={onRetry} />;
  }

  if (isLoading) {
    return <Skeleton active paragraph={{ rows }} />;
  }

  return <>{children}</>;
}

/** Shown when the signed-in user lacks the permission a screen needs (spec section 65). */
export function NotPermitted() {
  const { t } = useTranslation();

  return (
    <Result
      status="403"
      title="403"
      // Was common.error ("Something went wrong"), which reads as a fault rather than as the
      // deliberate answer it is — the screen loaded correctly, the permission is simply absent.
      subTitle={t('common.notPermitted')}
      extra={<Typography.Text type="secondary">{t('errors.PERMISSION_MISSING')}</Typography.Text>}
    />
  );
}
